import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapPin, CalendarPlus, Loader2, Trash2, Sparkles, Check, X, Clock } from 'lucide-react';

const CARD = 'relative overflow-hidden rounded-2xl bg-zinc-900/50 backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.22)]';

/* Section réservée au COACHING HYBRIDE (VIP) — réservation de séances présentiel à Nyon.
   Pour les clients "en ligne" (non hybride) : carte d'upsell comparative online vs hybride. */
export const PresentielSection = ({ en, api }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [flags, setFlags] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ preferred_date: '', preferred_time: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadBookings = useCallback(() => {
    api('/api/tracking/presentiel/bookings').then((d) => setBookings(d.bookings || [])).catch(() => setBookings([]));
  }, [api]);

  useEffect(() => {
    let active = true;
    api('/api/tracking/subscription')
      .then((f) => { if (active) { setFlags(f); if (f.hybrid) loadBookings(); } })
      .catch(() => active && setFlags({}));
    return () => { active = false; };
  }, [api, loadBookings]);

  if (!flags || !flags.coaching) return null;

  /* ---------- ONLINE (coaching mais PAS hybride) : upsell comparatif ---------- */
  if (!flags.hybrid) {
    const rows = [
      [L('Programmes de musculation personnalisés', 'Personalized training programs'), true, true],
      [L('Visio mensuelle avec Laurent', 'Monthly video call with Laurent'), true, true],
      [L('Messagerie 24/7', '24/7 messaging'), true, true],
      [L('Suivi nutrition & progrès', 'Nutrition & progress tracking'), true, true],
      [L('Séances en présentiel au studio de Nyon', 'In-person sessions at the Nyon studio'), false, true],
      [L('Corrections techniques en salle', 'In-studio technique corrections'), false, true],
      [L('Réservation prioritaire de créneaux', 'Priority slot booking'), false, true],
    ];
    return (
      <div className={`${CARD} p-6`} data-testid="hybrid-upsell">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shrink-0">
            <MapPin className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{L('Votre formule', 'Your plan')}</p>
            <h3 className="font-bold text-white text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {L('Coaching en ligne → passez à l\'Hybride', 'Online coaching → upgrade to Hybrid')}
            </h3>
          </div>
        </div>
        <p className="text-zinc-400 text-sm mt-1 mb-4">
          {L("Ajoutez le présentiel à Nyon à votre accompagnement en ligne pour accélérer vos résultats.",
             'Add in-person sessions in Nyon to your online coaching to accelerate your results.')}
        </p>

        <div className="rounded-xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto] text-xs">
            <div className="px-3 py-2 bg-white/5 font-semibold text-zinc-300">{L('Fonctionnalité', 'Feature')}</div>
            <div className="px-3 py-2 bg-white/5 font-semibold text-zinc-400 text-center w-20">{L('En ligne', 'Online')}</div>
            <div className="px-3 py-2 bg-primary/15 font-bold text-white text-center w-20">{L('Hybride', 'Hybrid')}</div>
            {rows.map(([label, online, hybrid], i) => (
              <React.Fragment key={i}>
                <div className="px-3 py-2 border-t border-white/5 text-zinc-300">{label}</div>
                <div className="px-3 py-2 border-t border-white/5 text-center">
                  {online ? <Check className="w-4 h-4 text-green-400 mx-auto" /> : <X className="w-4 h-4 text-zinc-600 mx-auto" />}
                </div>
                <div className="px-3 py-2 border-t border-white/5 bg-primary/5 text-center">
                  {hybrid ? <Check className="w-4 h-4 text-primary mx-auto" /> : <X className="w-4 h-4 text-zinc-600 mx-auto" />}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <a href="/offres" data-testid="upgrade-hybrid-button"
           className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] active:scale-95 transition-transform">
          <Sparkles className="w-4 h-4" /> {L('Passer au Coaching Hybride', 'Upgrade to Hybrid Coaching')}
        </a>
      </div>
    );
  }

  /* ---------- HYBRIDE (VIP) : réservation de séances présentiel ---------- */
  const submit = async (e) => {
    e.preventDefault();
    if (!form.preferred_date) { setMsg('need_date'); return; }
    setSubmitting(true); setMsg(null);
    try {
      await api('/api/tracking/presentiel/request', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      setForm({ preferred_date: '', preferred_time: '', note: '' });
      setMsg('ok'); loadBookings();
    } catch { setMsg('err'); } finally { setSubmitting(false); }
  };
  const cancel = async (id) => {
    try { await api(`/api/tracking/presentiel/${id}`, { method: 'DELETE' }); loadBookings(); } catch { /* noop */ }
  };
  const statusLabel = (s) => ({ pending: L('En attente', 'Pending'), confirmed: L('Confirmée', 'Confirmed'), done: L('Terminée', 'Done') }[s] || s);

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className={`${CARD} p-6`} data-testid="presentiel-section">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shrink-0">
          <MapPin className="w-4.5 h-4.5 text-white" />
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{L('Coaching Hybride', 'Hybrid coaching')}</p>
          <h3 className="font-bold text-white text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {L('Présentiel & Réservations', 'In-person & Bookings')}
          </h3>
        </div>
      </div>
      <p className="text-zinc-400 text-sm mt-1 mb-4">
        {L('Réservez une séance au studio de Nyon. Laurent vous confirmera le créneau.',
           'Book a session at the Nyon studio. Laurent will confirm your slot.')}
      </p>

      <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mb-5" data-testid="presentiel-form">
        <div>
          <label className="block text-[11px] text-zinc-400 mb-1">{L('Date souhaitée', 'Preferred date')}</label>
          <input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                 className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
                 data-testid="presentiel-date" required />
        </div>
        <div>
          <label className="block text-[11px] text-zinc-400 mb-1">{L('Heure (optionnel)', 'Time (optional)')}</label>
          <input type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
                 className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
                 data-testid="presentiel-time" />
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60"
                  data-testid="presentiel-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
            {L('Demander', 'Request')}
          </button>
        </div>
        <div className="sm:col-span-3">
          <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                 placeholder={L('Une note pour Laurent (objectif de la séance…)', 'A note for Laurent (session goal…)')}
                 className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
                 data-testid="presentiel-note" />
        </div>
      </form>

      {msg === 'ok' && <p className="text-green-400 text-sm mb-3" data-testid="presentiel-success">{L('Demande envoyée ! Laurent vous confirmera bientôt.', 'Request sent! Laurent will confirm shortly.')}</p>}
      {msg === 'need_date' && <p className="text-amber-400 text-sm mb-3">{L('Choisissez une date.', 'Please pick a date.')}</p>}
      {msg === 'err' && <p className="text-red-400 text-sm mb-3">{L('Une erreur est survenue. Réessayez.', 'Something went wrong. Try again.')}</p>}

      <div className="space-y-2" data-testid="presentiel-list">
        {bookings.length === 0 ? (
          <p className="text-zinc-500 text-sm">{L('Aucune séance présentiel pour le moment.', 'No in-person session yet.')}</p>
        ) : bookings.map((b) => (
          <div key={b.booking_id} className="flex items-center justify-between gap-3 rounded-lg bg-white/5 border border-white/10 px-3 py-2">
            <div className="flex items-center gap-2 text-sm text-white">
              <Clock className="w-4 h-4 text-primary shrink-0" />
              <span>{b.preferred_date}{b.preferred_time ? ` · ${b.preferred_time}` : ''}</span>
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">{statusLabel(b.status)}</span>
            </div>
            <button onClick={() => cancel(b.booking_id)} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid={`presentiel-cancel-${b.booking_id}`} aria-label="Annuler">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default PresentielSection;
